import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Button } from './ui/button';

const Header = ({ onOpenContactForm }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const navLinks = [
    { label: 'Home', id: 'home' },
    { label: 'Services', id: 'services' },
    { label: 'Process', id: 'process' },
    { label: 'Pricing', id: 'pricing' },
    { label: 'Testimonials', id: 'testimonials' },
    { label: 'FAQ', id: 'faq' },
    { label: 'Blog', id: 'blog' },
  ];

  return (
    <header data-testid="main-header" className="fixed top-0 left-0 right-0 z-50">
      {/* Amber accent line */}
      <div className="h-[2px] bg-gradient-to-r from-amber-500 via-orange-500 to-amber-500" />

      {/* Phone strip */}
      <div className="bg-[#101423]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex items-center justify-end py-1.5 text-xs">
          <a href="tel:+18001234567" className="flex items-center gap-1.5 text-amber-500 font-bold hover:text-amber-400 transition-colors">
            <Phone className="w-3 h-3" />
            +1 (800) 123-4567
          </a>
        </div>
      </div>

      {/* Main nav */}
      <div className={`transition-all duration-300 ${
        isScrolled
          ? 'bg-[#030308]/90 backdrop-blur-xl border-b border-white/5'
          : 'bg-[#030308]/60 backdrop-blur-md'
      }`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-4">
          <div className="flex items-center justify-between">
            <button
              data-testid="header-logo"
              onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              className="flex items-center gap-3 cursor-pointer group"
            >
              <div className="w-8 h-8 bg-gradient-to-br from-amber-500 to-orange-600 rounded-md flex items-center justify-center">
                <span className="text-white font-black text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>T</span>
              </div>
              <span className="text-base tracking-tight text-white font-normal hidden sm:block uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Trademark My LLC
              </span>
            </button>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-0.5">
              {navLinks.map(link => (
                <button
                  key={link.id}
                  data-testid={`nav-${link.id}`}
                  onClick={() => scrollToSection(link.id)}
                  className="px-4 py-2 text-sm text-slate-400 hover:text-amber-400 rounded-md transition-colors relative group font-medium"
                >
                  {link.label}
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-px bg-amber-500 group-hover:w-full transition-all duration-300" />
                </button>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-3">
              <Button
                data-testid="header-cta-button"
                onClick={onOpenContactForm}
                className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-bold px-6 h-9 text-xs rounded-md uppercase tracking-wider btn-shine shadow-lg shadow-amber-500/20"
              >
                Free Consultation
              </Button>
            </div>

            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-slate-300 p-1"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile menu */}
          {isMobileMenuOpen && (
            <div data-testid="mobile-menu" className="lg:hidden mt-3 pb-4 border-t border-white/10 pt-4">
              <nav className="flex flex-col gap-1">
                {navLinks.map(link => (
                  <button
                    key={link.id}
                    data-testid={`mobile-nav-${link.id}`}
                    onClick={() => scrollToSection(link.id)}
                    className="text-left px-3 py-2.5 text-sm text-slate-400 hover:text-amber-400 hover:bg-white/5 rounded-md transition-colors font-medium"
                  >
                    {link.label}
                  </button>
                ))}
                <div className="pt-3 mt-2 border-t border-white/10">
                  <a href="tel:+18001234567" className="flex items-center gap-2 text-sm text-amber-500 mb-3 px-3 font-bold">
                    <Phone className="w-4 h-4" /> +1 (800) 123-4567
                  </a>
                  <Button
                    data-testid="mobile-cta-button"
                    onClick={onOpenContactForm}
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-bold h-10 rounded-md uppercase tracking-wider btn-shine"
                  >
                    Free Consultation
                  </Button>
                </div>
              </nav>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
